// src/components/common/CalendarButton.jsx

import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton as MuiIconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Google as GoogleIcon,
  Apple as AppleIcon,
  Add as AddIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { isAppleDevice, isGoogleDevice, isPcDevice } from '../../_utils/deviceDetector';
import { generateIcs, generateMultipleIcs, downloadIcs, openGoogleCalendar } from '../../_utils/icsGenerator';

const CalendarButton = ({ 
  data,
  filename = 'calendar.ics',
  t = (key) => key,
  buttonProps = {}
}) => {
  // STATE
  const [showGoogleDialog, setShowGoogleDialog] = useState(false);
  const [showMenu, setShowMenu] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // VALIDAZIONE DATI
  const isMultiple = Array.isArray(data);
  const events = isMultiple ? data : [data];
  
  const hasValidData = events.length > 0 && events.every(e => 
    e?.title && 
    e?.start instanceof Date && 
    e?.end instanceof Date &&
    !isNaN(e.start.getTime()) &&
    !isNaN(e.end.getTime())
  );

  // RILEVAMENTO DISPOSITIVO
  const isApple = isAppleDevice();
  const isGoogle = isGoogleDevice();
  const isPc = isPcDevice();

  // ==========================================
  // DOWNLOAD SECCO - IDENTICO ALLA FUNZIONE ORIGINALE
  // ==========================================
  const handleDownload = () => {
    setIsLoading(true);
    try {
      const content = isMultiple ? generateMultipleIcs(events) : generateIcs(data);
      const name = isMultiple ? filename : `${data.title || 'evento'}.ics`;
      downloadIcs(content, name);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // HANDLER PRINCIPALE - CLICK SUL PULSANTE
  // ==========================================
  const handleClick = (event) => {
    if (!hasValidData) {
      console.warn('CalendarButton: dati evento non validi', data);
      return;
    }

    // APPLE → DOWNLOAD SECCO (come prima!)
    if (isApple) {
      handleDownload();
      return;
    }

    // GOOGLE → GOOGLE DIRETTO (link a Google Calendar)
    if (isGoogle) {
      if (isMultiple) {
        setShowGoogleDialog(true);
      } else {
        openGoogleCalendar(data);
      }
      return;
    }

    // PC → MENU CON 3 OPZIONI
    if (isPc) {
      setShowMenu(event.currentTarget);
      return;
    }

    // FALLBACK → DOWNLOAD SECCO
    handleDownload();
  };

  // ==========================================
  // HANDLER MENU PC
  // ==========================================
  const handleMenuClose = () => {
    setShowMenu(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();
    switch (action) {
      case 'apple':
        handleDownload();
        break;
      case 'google':
        if (isMultiple) {
          setShowGoogleDialog(true);
        } else {
          openGoogleCalendar(data);
        }
        break;
      case 'ics':
        handleDownload();
        break;
      default:
        break;
    }
  };

  // ==========================================
  // HANDLER GOOGLE DIALOG (multipli)
  // ==========================================
  const handleAddSingleEvent = (event) => {
    openGoogleCalendar(event);
  };

  const handleDownloadAllFromDialog = () => {
    const content = generateMultipleIcs(events);
    downloadIcs(content, filename);
    setShowGoogleDialog(false);
  };

  // ==========================================
  // FORMATTAZIONE
  // ==========================================
  const formatDate = (date) => {
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  // ==========================================
  // TOOLTIP DINAMICO
  // ==========================================
  const getTooltip = () => {
    if (isApple) return t('Apple') || 'Aggiungi a Apple Calendar';
    if (isGoogle) return t('Google') || 'Aggiungi a Google Calendar';
    return t('Calendario') || 'Aggiungi al calendario';
  };

  // ==========================================
  // MENU ITEMS PC
  // ==========================================
  const menuItems = [
    {
      key: 'apple',
      icon: <AppleIcon fontSize="small" sx={{ color: '#555' }} />,
      label: t('Apple') || 'Apple Calendar',
    },
    {
      key: 'google',
      icon: <GoogleIcon fontSize="small" sx={{ color: '#4285F4' }} />,
      label: t('Google') || 'Google Calendar',
    },
    {
      key: 'ics',
      icon: <DownloadIcon fontSize="small" />,
      label: t('File ICS') || 'Scarica ICS',
    }
  ];

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <>
      {/* PULSANTE */}
      <Tooltip title={getTooltip()}>
        <IconButton 
          onClick={handleClick} 
          disabled={isLoading || !hasValidData}
          {...buttonProps}
          sx={{ 
            color: '#2c5282',
            '&:hover': { backgroundColor: 'rgba(44, 82, 130, 0.08)' },
            ...buttonProps.sx 
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : <CalendarIcon sx={{ fontSize: 28 }} />}
        </IconButton>
      </Tooltip>

      {/* MENU PC */}
      <Menu
        anchorEl={showMenu}
        open={Boolean(showMenu)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 140,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
            mt: 1,
          }
        }}
      >
        {menuItems.map((item) => (
          <MenuItem 
            key={item.key} 
            onClick={() => handleMenuAction(item.key)}
            sx={{ py: 1.5, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                sx: { fontWeight: 500 }
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* DIALOG GOOGLE - EVENTI MULTIPLI */}
      <Dialog 
        open={showGoogleDialog} 
        onClose={() => setShowGoogleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GoogleIcon sx={{ color: '#4285F4' }} />
          <span>{t('Google') || 'Aggiungi a Google Calendar'}</span>
          <Chip 
            label={`${events.length} eventi`} 
            size="small" 
            sx={{ ml: 'auto' }}
          />
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('Clicca su [+] per aggiungere il singolo evento.') || 'Clicca su [+] per aggiungere un evento a Google Calendar.'}
          </Typography>

          <List dense disablePadding>
            {events.map((event, index) => (
              <ListItem
                key={index}
                disablePadding
                divider={index < events.length - 1}
                secondaryAction={
                  <MuiIconButton 
                    edge="end" 
                    size="small"
                    onClick={() => handleAddSingleEvent(event)}
                    sx={{ 
                      color: '#4285F4',
                      '&:hover': { backgroundColor: 'rgba(66, 133, 244, 0.08)' }
                    }}
                  >
                    <AddIcon />
                  </MuiIconButton>
                }
              >
                <ListItemButton onClick={() => handleAddSingleEvent(event)}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {event.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        📅 {formatDate(event.start)} • {formatTime(event.start)} - {formatTime(event.end)}
                        {event.location && ` • 📍 ${event.location}`}
                      </Typography>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
          <Button 
            onClick={handleDownloadAllFromDialog}
            variant="contained" 
            startIcon={<DownloadIcon />}
            sx={{ mb: 1 }}
          >
            {t('Scarica File ICS ed Importa') || '📥 Scarica tutti gli eventi come ICS'}
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setShowGoogleDialog(false)}>
              {t('chiudi') || 'Chiudi'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarButton;